import { useEffect, useState } from 'react';
import { Button, Card, Pill } from '@elysian/ui';
import type { FiscalYear } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, longDate, shortDate } from '../invoices/shared';

const KIND = { pack: 'Accountant’s pack', saft: 'SAF-T file' } as const;

function save(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** The most recent year that has ended, else the current one to date. */
function defaultRange(years: FiscalYear[], today: string): { from: string; to: string } {
  const ended = years.filter((y) => y.ends_on < today).sort((a, b) => b.ends_on.localeCompare(a.ends_on))[0];
  if (ended) return { from: ended.starts_on, to: ended.ends_on };
  const current = years.find((y) => y.starts_on <= today && today <= y.ends_on);
  return current ? { from: current.starts_on, to: today } : { from: `${today.slice(0, 4)}-01-01`, to: today };
}

/**
 * The whole books for the accountant (a zip of spreadsheets) or for an
 * auditor (SAF-T). Owners, admins and accountants only; every export is logged.
 */
export function BookExports() {
  const { source, can, today, context } = useSession();
  const toast = useToast((s) => s.show);
  const now = iso(today());
  const years = useAsync(() => source.fiscalYears(), [source]);
  const [version, setVersion] = useState(0);
  const log = useAsync(() => source.bookExports(), [source, version], { keep: true });
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);
  const [busy, setBusy] = useState<'pack' | 'saft' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!range && years.state === 'ready') setRange(defaultRange(years.data, now));
  }, [range, years, now]);

  if (!can('books.export')) return null;
  const name = context?.organisation.legal_name ?? 'Books';
  const { from, to } = range ?? { from: `${now.slice(0, 4)}-01-01`, to: now };
  const yearList = years.state === 'ready' ? [...years.data].sort((a, b) => b.starts_on.localeCompare(a.starts_on)) : [];

  async function download(kind: 'pack' | 'saft') {
    setBusy(kind);
    setError(null);
    try {
      const blob = kind === 'pack' ? await source.exportAccountantPack(from, to) : await source.exportSaft(from, to);
      const safe = name.replace(/[^\p{L}\p{N} &'.-]+/gu, ' ').replace(/\s+/g, ' ').trim() || 'Books';
      save(blob, kind === 'pack' ? `Accounts ${safe} ${from} to ${to}.zip` : `SAF-T ${safe} ${from} to ${to}.xml`);
      toast(`${KIND[kind]} downloaded`);
      setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card padding="20px">
      <h2 className="el-h2" style={{ margin: 0 }}>For your accountant or an auditor</h2>
      <p className="el-hint" style={{ margin: '4px 0 16px' }}>The whole books for a period, ready to send. Each download is recorded below.</p>

      <div className="el-row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }} role="group" aria-label="Period to export">
        {yearList.map((y) => {
          const toDate = y.ends_on < now ? y.ends_on : now;
          const on = from === y.starts_on && to === toDate;
          return (
            <Button key={y.id} size="sm" variant={on ? 'primary' : 'ghost'} aria-pressed={on} onClick={() => setRange({ from: y.starts_on, to: toDate })}>
              {`FY${y.ends_on.slice(0, 4)}`}{y.ends_on >= now ? ' so far' : ''}
            </Button>
          );
        })}
        <input type="date" className="el-input" aria-label="Export from" value={from} style={{ width: 'auto' }}
          onChange={(e) => setRange({ from: e.target.value, to })} />
        <span className="el-hint">to</span>
        <input type="date" className="el-input" aria-label="Export to" value={to} style={{ width: 'auto' }}
          onChange={(e) => setRange({ from, to: e.target.value })} />
      </div>

      <div className="el-export-choices" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 16 }}>
        <section aria-label={KIND.pack} style={{ border: '1px solid var(--el-line-strong)', borderRadius: 10, padding: 16 }}>
          <strong>Accountant’s pack</strong> <Pill tone="info">.zip</Pill>
          <p className="el-hint" style={{ margin: '6px 0 12px' }}>
            Spreadsheets of the trial balance, nominal ledger, profit and loss, balance sheet, customer and supplier balances,
            VAT returns filed and journals posted by hand. For the year-end accounts.
          </p>
          <Button variant="primary" disabled={busy !== null} onClick={() => void download('pack')}>
            {busy === 'pack' ? 'Preparing…' : 'Download the pack'}
          </Button>
        </section>
        <section aria-label={KIND.saft} style={{ border: '1px solid var(--el-line-strong)', borderRadius: 10, padding: 16 }}>
          <strong>SAF-T file</strong> <Pill tone="info">.xml</Pill>
          <p className="el-hint" style={{ margin: '6px 0 12px' }}>
            The Standard Audit File for Tax (OECD 2.0): every transaction, account, customer, supplier and invoice, in the format
            audit software reads. For Revenue or HMRC if they ask for your records electronically.
          </p>
          <Button disabled={busy !== null} onClick={() => void download('saft')}>
            {busy === 'saft' ? 'Preparing…' : 'Download SAF-T'}
          </Button>
        </section>
      </div>
      {error && <div className="el-error-box" role="alert" style={{ marginTop: 12 }}>{error}</div>}

      <h3 style={{ margin: '20px 0 8px', fontSize: 14 }}>Recent exports</h3>
      {log.state === 'ready' && log.data.length === 0 && <p className="el-muted" style={{ margin: 0 }}>The books haven’t been exported yet.</p>}
      {log.state === 'ready' && log.data.length > 0 && (
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Recent exports">
            <thead><tr><th scope="col">When</th><th scope="col">What</th><th scope="col">Period</th><th scope="col">By</th></tr></thead>
            <tbody>
              {log.data.map((e, i) => (
                <tr key={`${e.created_at}-${i}`}>
                  <td>{shortDate(e.created_at.slice(0, 10))} {e.created_at.slice(11, 16)}</td>
                  <td>{KIND[e.kind]}</td>
                  <td>{shortDate(e.from)} – {shortDate(e.to)}</td>
                  <td>{e.created_by.replace(/^.*:/, '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="el-hint" style={{ margin: '12px 0 0' }}>Exports cover {longDate(from)} to {longDate(to)}. At most two years at a time.</p>
    </Card>
  );
}
